'use client';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { usePageAction } from '@repo/hooks/usePageAction';
import { useRef } from 'react';
import type { Category } from '../Categories';
import { DEFAULT_COLORS } from '../constants';

interface ChartLegendItemProps {
  item: Category;
  index: number;
}

export function ChartLegendItemAccordion({ item, index }: ChartLegendItemProps) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const trackLegendItemInteraction = usePageAction({
    actionName: 'banking_rating_methodology',
    actionSection: 'banking',
  });

  return (
    <Box
      component="details"
      ref={detailsRef}
      className="star-rating-categories-legend-item"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderBottom: '1px solid',
        borderColor: 'neutral.lighter2',
        '&:hover': {
          borderColor: item.color ?? DEFAULT_COLORS[index],
          cursor: 'pointer',
        },
      }}
      onClick={() => {
        trackLegendItemInteraction();
      }}
    >
      <Box
        component="summary"
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          listStyle: 'none',
          paddingY: '12px',
          '&::-webkit-details-marker': {
            display: 'none',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            marginRight: '50px',
          }}
        >
          <Box
            sx={{
              flexShrink: 0,
              display: 'inline-block',
              width: '14px',
              height: '14px',
              backgroundColor: item.color ?? DEFAULT_COLORS[index],
              borderRadius: '4px',
            }}
          />
          <Typography
            component="span"
            sx={{
              fontSize: '16px',
              fontWeight: '500',
              marginLeft: '8px',
              color: 'neutral.darker',
            }}
          >
            {item.label}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '8px' }}>
          <Typography sx={{ fontSize: '16px', color: 'neutral.dark' }}>{item.value}%</Typography>
          <ExpandMoreIcon
            sx={{
              color: 'neutral.light',
              fontSize: '24px',
              transition: 'transform 0.3s ease',
              '&:hover': {
                color: 'neutral.darker',
              },
              '[open] > summary &': {
                transform: 'rotate(180deg)',
                color: 'neutral.darker',
              },
            }}
          />
        </Box>
      </Box>
      <Typography
        component="p"
        variant="textSmall"
        sx={{ color: 'neutral.dark', paddingBottom: 3 }}
      >
        {item.tooltipLabel}
      </Typography>
    </Box>
  );
}
